import { useState } from "react";
import { Card, CardContent } from "@app/components/ui/card";
import { Button } from "@app/components/ui/button";
import { Badge } from "@app/components/ui/badge";
import { Input } from "@app/components/ui/input";
import { Label } from "@app/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@app/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@app/components/ui/select";
import {
  Plus,
  Pencil,
  Trash2,
  Settings2,
  MessageSquare,
  Thermometer,
  Cpu,
} from "lucide-react";
import { Trans, useLingui } from "@lingui/macro";

// Types

/**
 * One LLM app declared in the backend registry (``llm_registry.py``).
 * The served model + endpoint URL are owned by the registry (model_alias) and
 * the ManagedLLMApp table (app id), so a PromptStack only references the app by
 * its stable ``key`` — it never stores a model or URL of its own.
 */
export interface LlmApp {
  key: string;
  /**
   * Who serves the model: ``ai_deploy`` = an app of ours on a GPU (named by
   * ``app_name``, in ``region``, woken on demand), ``ai_endpoints`` = OVH's
   * serverless gateway, which has no app of ours and so no ``app_name``.
   */
  provider: "ai_deploy" | "ai_endpoints";
  app_name: string | null;
  model_alias: string;
  region: string;
}

/** Human label for a provider — product names, identical in every locale. */
const PROVIDER_LABEL: Record<LlmApp["provider"], string> = {
  ai_deploy: "OVH AI Deploy",
  ai_endpoints: "OVH AI Endpoints",
};

export interface PromptStackConfig {
  id: string;
  title: string;
  prompt: string | null;
  temperature: number | null;
  comment: string | null;
  created_at: string | null;
  /**
   * Which registry LLM app this config runs on (``llm_registry`` key, e.g.
   * "reasoning"). The served model + endpoint are DERIVED from it at load time
   * by the backend ``llm_app_resolver`` — they are not stored here.
   */
  llm_app_key: string | null;
  /**
   * Derived (read-only) endpoint values the backend fills from the registry.
   * Shown for reference only; never edited from this form.
   */
  model: string | null;
  api_base_url: string | null;
  /** Derived from the registry app; null when the row points at no registry app. */
  provider: LlmApp["provider"] | null;
  /**
   * Override for the vLLM ``chat_template_kwargs.enable_thinking`` flag.
   * - ``null`` keeps the server default (reasoning models default to off).
   * - ``true`` / ``false`` is injected explicitly on every call.
   */
  enable_thinking: boolean | null;
  /**
   * Few-shot selection strategy.
   * - ``null`` keeps legacy RAG-with-static-fallback behaviour.
   * - ``"rag"`` / ``"static"`` / ``"none"``.
   */
  few_shot_strategy: string | null;
}

// The write payload only carries user-editable fields (the endpoint is derived).
export type PromptStackWrite = Pick<
  PromptStackConfig,
  | "title"
  | "prompt"
  | "temperature"
  | "comment"
  | "llm_app_key"
  | "enable_thinking"
  | "few_shot_strategy"
>;

interface PromptStackManagerProps {
  configs: PromptStackConfig[];
  /** LLM apps from the registry (backend ``GET /prompt-stack/llm-apps``). */
  llmApps: LlmApp[];
  onAdd: (config: PromptStackWrite) => void;
  onUpdate: (configId: string, updates: Partial<PromptStackWrite>) => void;
  onDelete: (configId: string) => void;
  loading?: boolean;
  /** When true, hides all write actions (the demo, a reader visiting another speciality). */
  readOnly?: boolean;
}

/** Model served by the app a config references (derived, for display). */
function modelForConfig(
  config: Pick<PromptStackConfig, "llm_app_key" | "model">,
  llmApps: LlmApp[],
): string | null {
  const app = llmApps.find((a) => a.key === config.llm_app_key);
  return app?.model_alias ?? config.model ?? null;
}

/**
 * Who serves the model this config runs on, or null for a row pinned to an
 * explicit endpoint (a local llama-server) rather than to a registry app.
 */
function providerForConfig(
  config: Pick<PromptStackConfig, "llm_app_key" | "provider">,
  llmApps: LlmApp[],
): LlmApp["provider"] | null {
  const app = llmApps.find((a) => a.key === config.llm_app_key);
  return app?.provider ?? config.provider ?? null;
}

// ----- Dialog d'édition -----
function PromptStackEditorDialog({
  open,
  onClose,
  onSave,
  initialData,
  llmApps,
  title,
}: {
  open: boolean;
  onClose: () => void;
  onSave: (data: PromptStackWrite) => void;
  initialData?: PromptStackConfig;
  llmApps: LlmApp[];
  title?: string;
}) {
  const { t } = useLingui();
  const [name, setName] = useState(initialData?.title || "");
  const [llmAppKey, setLlmAppKey] = useState(
    initialData?.llm_app_key || llmApps[0]?.key || "",
  );
  const [temperature, setTemperature] = useState(
    initialData?.temperature ?? 0.7,
  );
  const [prompt, setPrompt] = useState(initialData?.prompt || "");
  const [comment, setComment] = useState(initialData?.comment || "");
  // Tri-state: "default" → null (server default), "on" → true, "off" → false.
  const [thinkingMode, setThinkingMode] = useState<"default" | "on" | "off">(
    initialData?.enable_thinking === true
      ? "on"
      : initialData?.enable_thinking === false
        ? "off"
        : "default",
  );
  // Few-shot strategy: "default" → null (legacy = rag-with-fallback), or
  // an explicit "rag" / "static" / "none".
  const [fewShotStrategy, setFewShotStrategy] = useState<
    "default" | "rag" | "static" | "none"
  >(
    initialData?.few_shot_strategy === "rag" ||
      initialData?.few_shot_strategy === "static" ||
      initialData?.few_shot_strategy === "none"
      ? initialData.few_shot_strategy
      : "default",
  );
  const [error, setError] = useState<string | null>(null);

  const isEditing = !!initialData;
  const selectedApp = llmApps.find((a) => a.key === llmAppKey);

  const handleSave = () => {
    setError(null);
    if (!name.trim()) {
      setError(t`Le nom est requis`);
      return;
    }
    if (!llmAppKey) {
      setError(t`Sélectionnez une app LLM du registre`);
      return;
    }

    const data: PromptStackWrite = {
      title: name.trim(),
      llm_app_key: llmAppKey,
      temperature,
      prompt: prompt.trim() || null,
      comment: comment.trim() || null,
      enable_thinking:
        thinkingMode === "on" ? true : thinkingMode === "off" ? false : null,
      few_shot_strategy:
        fewShotStrategy === "default" ? null : fewShotStrategy,
    };

    onSave(data);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title ?? t`Ajouter une configuration`}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Nom */}
          <div>
            <Label htmlFor="config-name"><Trans>Nom *</Trans></Label>
            <Input
              id="config-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex. : Extraction médicale v2"
            />
          </div>

          {/* App LLM (registre) */}
          <div>
            <Label><Trans>App LLM (registre) *</Trans></Label>
            <Select value={llmAppKey} onValueChange={setLlmAppKey}>
              <SelectTrigger className="mt-1">
                <SelectValue placeholder={t`Sélectionner une app LLM...`} />
              </SelectTrigger>
              <SelectContent>
                {llmApps.map((app) => (
                  <SelectItem key={app.key} value={app.key}>
                    {app.key} — {app.model_alias} ({PROVIDER_LABEL[app.provider]})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-400 mt-1">
              <Trans>
                Le modèle et l'URL d'inférence sont dérivés automatiquement de
                l'app choisie (registre + table ManagedLLMApp) — plus rien à
                saisir à la main.
              </Trans>
            </p>
            {selectedApp && (
              <div className="mt-2 flex items-center gap-2 flex-wrap text-xs">
                <Badge variant="outline" className="font-mono">
                  {selectedApp.model_alias}
                </Badge>
                <Badge
                  variant="outline"
                  className="bg-blue-50 text-blue-900 border-blue-200"
                >
                  {PROVIDER_LABEL[selectedApp.provider]}
                </Badge>
                <span className="text-slate-400 font-mono">
                  {selectedApp.provider === "ai_endpoints" ? (
                    <Trans>sans serveur — facturé au token</Trans>
                  ) : (
                    `${selectedApp.app_name} · ${selectedApp.region}`
                  )}
                </span>
              </div>
            )}
          </div>

          {/* Température */}
          <div>
            <Label htmlFor="config-temp"><Trans>Température ({temperature})</Trans></Label>
            <input
              id="config-temp"
              type="range"
              min="0"
              max="2"
              step="0.1"
              value={temperature}
              onChange={(e) => setTemperature(parseFloat(e.target.value))}
              className="w-full mt-1 accent-primary"
            />
            <div className="flex justify-between text-xs text-slate-400 mt-0.5">
              <span><Trans>0 (déterministe)</Trans></span>
              <span><Trans>2 (créatif)</Trans></span>
            </div>
          </div>

          {/* Mode raisonnement (chat_template_kwargs.enable_thinking) */}
          <div>
            <Label><Trans>Mode raisonnement</Trans></Label>
            <div className="flex gap-2 mt-1">
              {(
                [
                  { value: "default", label: t`Par défaut du serveur` },
                  { value: "on", label: t`Activé` },
                  { value: "off", label: t`Désactivé` },
                ] as const
              ).map((opt) => (
                <Badge
                  key={opt.value}
                  variant={thinkingMode === opt.value ? "default" : "outline"}
                  className={`cursor-pointer px-3 py-1 ${
                    thinkingMode === opt.value ? "bg-primary text-white" : ""
                  }`}
                  onClick={() => setThinkingMode(opt.value)}
                >
                  {opt.label}
                </Badge>
              ))}
            </div>
            <p className="text-xs text-slate-400 mt-1">
              <Trans>
                Contrôle l'option `chat_template_kwargs.enable_thinking`
                envoyée à vLLM (Qwen3, …). « Par défaut du serveur »
                conserve l'historique (désactivé pour les modèles de
                raisonnement).
              </Trans>
            </p>
          </div>

          {/* Stratégie de sélection des exemples (few-shot) */}
          <div>
            <Label><Trans>Stratégie d'exemples</Trans></Label>
            <div className="flex gap-2 mt-1 flex-wrap">
              {(
                [
                  { value: "default", label: t`Par défaut (RAG)` },
                  { value: "rag", label: t`RAG (similarité)` },
                  { value: "static", label: t`Statique (sort_order)` },
                  { value: "none", label: t`Aucun exemple` },
                ] as const
              ).map((opt) => (
                <Badge
                  key={opt.value}
                  variant={
                    fewShotStrategy === opt.value ? "default" : "outline"
                  }
                  className={`cursor-pointer px-3 py-1 ${
                    fewShotStrategy === opt.value
                      ? "bg-primary text-white"
                      : ""
                  }`}
                  onClick={() => setFewShotStrategy(opt.value)}
                >
                  {opt.label}
                </Badge>
              ))}
            </div>
            <p className="text-xs text-slate-400 mt-1">
              <Trans>
                Détermine comment les exemples (few-shots) sont sélectionnés
                pour chaque champ. RAG utilise la similarité sémantique
                (pgvector) ; Statique prend les k premiers exemples par
                `sort_order` ; Aucun supprime entièrement les exemples du
                prompt.
              </Trans>
            </p>
          </div>

          {/* Prompt système */}
          <div>
            <Label htmlFor="config-prompt"><Trans>Prompt système</Trans></Label>
            <textarea
              id="config-prompt"
              className="w-full mt-1 border border-slate-200 rounded-lg p-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/50 min-h-[120px]"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder={t`Vous êtes un assistant médical spécialisé...`}
              rows={6}
            />
          </div>

          {/* Commentaire */}
          <div>
            <Label htmlFor="config-comment"><Trans>Commentaire</Trans></Label>
            <Input
              id="config-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={t`Notes internes...`}
            />
          </div>

          {error && (
            <div className="bg-red-50 text-red-700 p-3 rounded text-sm">
              {error}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            <Trans>Annuler</Trans>
          </Button>
          <Button onClick={handleSave}>
            {isEditing ? t`Mettre à jour` : t`Créer`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ----- Carte configuration -----
function PromptStackCard({
  config,
  llmApps,
  onEdit,
  onDelete,
  readOnly,
}: {
  config: PromptStackConfig;
  llmApps: LlmApp[];
  onEdit: () => void;
  onDelete: () => void;
  readOnly?: boolean;
}) {
  const { t } = useLingui();
  const derivedModel = modelForConfig(config, llmApps);
  const derivedProvider = providerForConfig(config, llmApps);

  return (
    <Card className="hover:shadow-md hover:border-primary/50 transition-all">
      <CardContent className="pt-4 pb-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            {/* Titre + badges */}
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="font-semibold text-slate-900 truncate">
                {config.title}
              </h4>
              {config.llm_app_key && (
                <Badge className="text-xs bg-blue-100 text-blue-800">
                  {config.llm_app_key}
                </Badge>
              )}
              {derivedModel && (
                <Badge variant="outline" className="text-xs font-mono">
                  {derivedModel}
                </Badge>
              )}
              {derivedProvider && (
                <Badge
                  variant="outline"
                  className="text-xs bg-blue-50 text-blue-900 border-blue-200"
                >
                  {PROVIDER_LABEL[derivedProvider]}
                </Badge>
              )}
              {config.few_shot_strategy && (
                <Badge
                  variant="outline"
                  className="text-xs bg-blue-50 text-blue-900 border-blue-200"
                  title={t`Stratégie d'exemples (few-shot)`}
                >
                  {t`shots`}: {config.few_shot_strategy}
                </Badge>
              )}
            </div>

            {/* Prompt aperçu */}
            {config.prompt && (
              <div className="mt-2 flex items-start gap-2">
                <MessageSquare className="h-3.5 w-3.5 text-slate-400 mt-0.5 shrink-0" />
                <p className="text-sm text-slate-500 line-clamp-2 font-mono">
                  {config.prompt}
                </p>
              </div>
            )}

            {/* Métadonnées */}
            <div className="mt-2 flex items-center gap-4 text-xs text-slate-400 flex-wrap">
              {config.temperature !== null && (
                <span className="flex items-center gap-1">
                  <Thermometer className="h-3 w-3" />
                  {config.temperature}
                </span>
              )}
              {config.comment && (
                <span className="truncate max-w-[200px]" title={config.comment}>
                  {config.comment}
                </span>
              )}
            </div>
          </div>

          {/* Actions */}
          {!readOnly && (
            <div className="flex gap-1 shrink-0">
              <Button variant="ghost" size="sm" onClick={onEdit} title={t`Modifier`}>
                <Pencil className="h-4 w-4 text-slate-500" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={onDelete}
                title={t`Supprimer`}
              >
                <Trash2 className="h-4 w-4 text-red-400" />
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ----- Composant principal -----
export default function PromptStackManager({
  configs,
  llmApps,
  onAdd,
  onUpdate,
  onDelete,
  readOnly,
}: PromptStackManagerProps) {
  const { t } = useLingui();
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState<PromptStackConfig | null>(
    null,
  );

  const handleAdd = (data: PromptStackWrite) => {
    onAdd(data);
  };

  const handleUpdate = (data: PromptStackWrite) => {
    if (editingConfig) {
      onUpdate(editingConfig.id, data);
    }
  };

  const handleDelete = (configId: string) => {
    if (!confirm(t`Êtes-vous sûr de vouloir supprimer cette configuration ?`))
      return;
    onDelete(configId);
  };

  // Résumé par app LLM du registre.
  const appCounts: Record<string, number> = {};
  for (const c of configs) {
    const key = c.llm_app_key || "—";
    appCounts[key] = (appCounts[key] || 0) + 1;
  }

  return (
    <div className="space-y-4">
      {/* Header + bouton ajouter */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 flex-wrap">
          {Object.entries(appCounts).map(([app, count]) => (
            <Badge key={app} className="text-xs bg-blue-100 text-blue-800">
              {app} ({count})
            </Badge>
          ))}
          {configs.length === 0 && (
            <span className="text-sm text-slate-400"><Trans>Aucune configuration</Trans></span>
          )}
        </div>
        {!readOnly && (
          <Button
            size="sm"
            onClick={() => {
              setEditingConfig(null);
              setEditorOpen(true);
            }}
          >
            <Plus className="h-4 w-4 mr-1" />
            <Trans>Ajouter une configuration</Trans>
          </Button>
        )}
      </div>

      {/* Liste des configurations */}
      {configs.length === 0 ? (
        <div className="text-center py-12">
          <Settings2 className="h-12 w-12 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500"><Trans>Aucune configuration LLM.</Trans></p>
          <p className="text-sm text-slate-400 mt-1">
            <Trans>Créez une configuration pour choisir l'app LLM, le prompt et les paramètres utilisés par les expériences.</Trans>
          </p>
          {!readOnly && (
            <Button
              className="mt-4"
              onClick={() => {
                setEditingConfig(null);
                setEditorOpen(true);
              }}
            >
              <Cpu className="h-4 w-4 mr-1" />
              <Trans>Créer une configuration</Trans>
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {configs.map((config) => (
            <PromptStackCard
              key={config.id}
              config={config}
              llmApps={llmApps}
              readOnly={readOnly}
              onEdit={() => {
                setEditingConfig(config);
                setEditorOpen(true);
              }}
              onDelete={() => handleDelete(config.id)}
            />
          ))}
        </div>
      )}

      {/* Dialog d'édition */}
      {editorOpen && (
        <PromptStackEditorDialog
          open={editorOpen}
          onClose={() => {
            setEditorOpen(false);
            setEditingConfig(null);
          }}
          onSave={editingConfig ? handleUpdate : handleAdd}
          initialData={editingConfig ?? undefined}
          llmApps={llmApps}
          title={
            editingConfig
              ? t`Modifier la configuration`
              : t`Ajouter une configuration`
          }
        />
      )}
    </div>
  );
}
